import { call, put, select } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { createSliceSaga } from "../saga-helper";
import {
  cancelSubscription,
  checkPaymentDone,
  getActiveSubscriptionDetails,
  getDownGradeSubscriptionPlan,
  getPaymentDetails,
  getPaymentMethods,
  getPlaymentHistory,
  getPlaymentLogs,
  getPurchaseSeatLogs,
  getSession,
  getUserCardDetails,
  handlePayment,
  paymentSeatWithTokenService,
  paymentWithToken,
  setDefalutSaveCardService,
  updatePaymentCardDetails,
} from "../../services/PaymentServices";
import { PaymentReducerAction } from "./slice";
import { PayloadAction } from "@reduxjs/toolkit";
import routeConstant from "../../utils/routeConstant";
import * as Sentry from "@sentry/react";
import { ActivePlanInfo, paymentHistoryListDataType } from "./types";
import { notify, ToastTypes } from "../../utils/helpers";
import {
  purchaseSeatLogsListSelector,
  userCardDetailsSelector,
} from "./selector";
import { selectFolderDetailsSelector } from "../metadata/selector";
import { folderItem, folderLists } from "../metadata/types";
import { FetchFolderListService } from "../../services/FolderService";
import { getRedirectUrl } from "../../utils/redirect";

const saga = createSliceSaga({
  name: "payment-state",
  caseSagas: {
    *initiateCheckout(
      action: PayloadAction<{
        type: string;
        subscriptionId: string;
        currency: string;
        BASE_URL: string;
        isSeat: boolean;
        seatCount?: number;
      }>
    ) {
      try {
        yield put(PaymentReducerAction.clearPaymentSession());
        const response: AxiosResponse = yield call(getSession, {
          type: action?.payload?.type,
          currency: action?.payload?.currency,
          subscriptionId: action?.payload?.subscriptionId,
          isSeat: action?.payload?.isSeat,
          seatCount: action?.payload?.seatCount,
        });

        if (response?.status === 200) {
          const updatedData = response.data.map((res: any) => {
            if (res.returnUrl) {
              return {
                ...res,
                returnUrl: `${action.payload.BASE_URL}${routeConstant?.redirect}?${res.returnUrl}`,
              };
            }
            return res;
          });
          yield put(
            PaymentReducerAction.setPaymentSession([
              updatedData,
              response.status,
            ])
          );
        }
      } catch (error) {
        return error;
      }
    },
    *initiateUpdatePaymentCard(
      action: PayloadAction<{
        type: string;
        updateCard: boolean;
        subscriptionId: string;
        currency: string;
        BASE_URL: string;
      }>
    ) {
      try {
        yield put(PaymentReducerAction.clearPaymentSession());
        const response: AxiosResponse = yield call(getSession, {
          type: action?.payload?.type,
          currency: action?.payload?.currency,
          subscriptionId: action?.payload?.subscriptionId,
          updateCard: action?.payload?.updateCard,
        });

        if (response?.status === 200) {
          const updatedData = response.data.map((res: any) => {
            if (res.returnUrl) {
              return {
                ...res,
                returnUrl: `${action.payload.BASE_URL}${routeConstant?.redirect}?${res.returnUrl}`,
              };
            }
            return res;
          });
          yield put(
            PaymentReducerAction.setPaymentSession([
              updatedData,
              response.status,
            ])
          );
        }
      } catch (error) {
        console.error("Error in initiateUpdatePaymentCard saga:", error);
      }
    },
    *getPaymentHistoryList() {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));
        const response: AxiosResponse = yield call(getPlaymentHistory);
        const activePlanresponse: AxiosResponse = yield call(
          getActiveSubscriptionDetails
        );

        if (activePlanresponse?.status === 200) {
          yield put(
            PaymentReducerAction.setActiveSubscription(
              activePlanresponse?.data?.result
            )
          );
        }

        if (response?.status === 200) {
          let paymentHistory: paymentHistoryListDataType[] =
            response?.data?.data;
          const total = response?.data?.total;

          const activePlanData: ActivePlanInfo | null =
            activePlanresponse?.data?.result || null;

          if (activePlanData) {
            const {
              activePlanId,
              upcomingPlanId,
              upcomingPlanIdStatus,
              planEndDate,
            } = activePlanData;

            paymentHistory = paymentHistory.map((item) => {
              const updatedItem = { ...item };

              if (item._id === activePlanId) {
                updatedItem.planStatus = "Current Plan";
                updatedItem.planEndDate = planEndDate;
              } else {
                delete updatedItem.planStatus;
                delete updatedItem.planEndDate;
              }

              if (item._id === upcomingPlanId) {
                if (upcomingPlanIdStatus === "Scheduled") {
                  updatedItem.planStatus = "Renewal Plan";
                } else if (item.status === "Cancelled") {
                  updatedItem.status = "Cancelled";
                  updatedItem.planStatus = "Cancelled Plan";
                }
              }

              return updatedItem;
            });
          }

          yield put(
            PaymentReducerAction.setPaymentHistory({
              data: paymentHistory,
              total: total,
            })
          );
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *getPaymentLogsList(
      action: PayloadAction<{
        [key: string]: any;
      }>
    ) {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));

        const response: AxiosResponse = yield call(
          getPlaymentLogs,
          action.payload
        );

        if (response?.status === 200) {
          yield put(
            PaymentReducerAction.setPaymentLogs({
              data: response?.data?.data?.data,
              total: response?.data?.data?.total,
            })
          );
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *getPurchaseSeatLogsList(
      action: PayloadAction<{
        skip: number;
        limit: number;
        [key: string]: any; // filters or sort options
      }>
    ) {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));

        const { skip } = action.payload;

        const response: AxiosResponse = yield call(
          getPurchaseSeatLogs,
          action.payload
        );

        if (response?.status === 200) {
          const newData = response?.data?.data?.data || [];
          const total = response?.data?.data?.total || 0;

          let mergedData = newData;

          // Append if it's a subsequent page
          if (skip > 0) {
            const existingList: { data: any[]; total: number } = yield select(
              purchaseSeatLogsListSelector
            );
            mergedData = [...existingList?.data, ...newData];
          }

          yield put(
            PaymentReducerAction.setPurchaseSeatLogs({
              data: mergedData,
              total,
            })
          );
        }
      } catch (error) {
        Sentry.captureException(error);
        throw new Error();
      } finally {
        yield put(PaymentReducerAction.setIsLoading(false));
      }
    },
    *getDownGradePlan(
      action: PayloadAction<{
        [key: string]: any;
      }>
    ) {
      try {
        yield put(PaymentReducerAction.setIsButtonLoading(true));
        const { planId, handleNavigate } = action.payload;
        const response: AxiosResponse = yield call(
          getDownGradeSubscriptionPlan,
          planId
        );
        if (response?.status === 200) {
          notify({
            message: response?.data?.message,
            type: ToastTypes.SUCCESS,
          });
          yield put(PaymentReducerAction.setIsButtonLoading(false));
          handleNavigate("Authorised");
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsButtonLoading(false));
        throw new Error();
      }
    },
    *getPaymentDetails(action: {
      payload: {
        id: string;
      };
    }) {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));
        const response: AxiosResponse = yield call(
          getPaymentDetails,
          action.payload.id
        );
        if (response?.status === 200) {
          yield put(PaymentReducerAction.setPaymentDetails(response?.data));
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *cancelSubscription() {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));
        const response: AxiosResponse = yield call(cancelSubscription);

        if (response?.status === 200) {
          yield put(PaymentSagaActions.getPaymentHistoryList());
          notify({
            message: "Subscription Cancelled!",
            type: ToastTypes.SUCCESS,
          });
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *GetUserCardDetails() {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));
        const response: AxiosResponse = yield call(getUserCardDetails);
        if (response?.status === 200) {
          yield put(
            PaymentReducerAction.setUserCardDetails(
              response?.data?.result || []
            )
          );
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *GetPaymentMethods() {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));
        const response: AxiosResponse = yield call(getPaymentMethods);
        if (response?.status === 200) {
          const cardPaymentMethods =
            response?.data?.result?.paymentMethods?.filter(
              (item: any) => item.type === "scheme"
            ) || [];
          yield put(PaymentReducerAction.setPaymentMethods(cardPaymentMethods));
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *handleCheckoutPayment(
      action: PayloadAction<{
        [key: string]: any;
      }>
    ) {
      try {
        const { data, handleNavigate } = action.payload;
        const response: AxiosResponse = yield call(handlePayment, data);

        if (response?.status === 200) {
          const updatedData = {
            ...response.data,
            returnUrl: `${action.payload.BASE_URL}${routeConstant?.redirect}?${response?.data?.returnUrl}`,
          };
          handleNavigate(response?.data?.resultCode);
          yield put(PaymentReducerAction.setpaymentResponse(updatedData));
        }
      } catch (error) {
        return error;
      }
    },
    *handleCheckoutPaymentWithToken(
      action: PayloadAction<{
        [key: string]: any;
      }>
    ) {
      try {
        yield put(PaymentReducerAction.setIsButtonLoading(true));
        const { data, handleNavigate } = action.payload;
        const response: AxiosResponse = yield call(paymentWithToken, data);

        if (response?.status === 200) {
          const updatedData = {
            ...response?.data?.result?.response,
            returnUrl: `${action.payload.BASE_URL}${routeConstant?.redirect}?${response?.data?.result?.response?.returnUrl}`,
          };
          handleNavigate(response?.data?.result?.response?.resultCode);
          yield put(PaymentReducerAction.setpaymentResponse(updatedData));
        }
        yield put(PaymentReducerAction.setIsButtonLoading(false));
      } catch (error) {
        yield put(PaymentReducerAction.setIsButtonLoading(false));
        return error;
      }
    },
    *handlePaymentSeatWithToken(
      action: PayloadAction<{
        [key: string]: any;
      }>
    ) {
      try {
        yield put(PaymentReducerAction.setIsButtonLoading(true));
        const { data, handleNavigate } = action.payload;
        const response: AxiosResponse = yield call(
          paymentSeatWithTokenService,
          data
        );

        if (response?.status === 200) {
          const updatedData = {
            ...response?.data?.result?.response,
            returnUrl: `${action.payload.BASE_URL}${routeConstant?.redirect}?${response?.data?.result?.response?.returnUrl}`,
          };
          handleNavigate(response?.data?.result?.response?.resultCode);
          yield put(PaymentReducerAction.setpaymentResponse(updatedData));
        }
        yield put(PaymentReducerAction.setIsButtonLoading(false));
      } catch (error) {
        yield put(PaymentReducerAction.setIsButtonLoading(false));
        return error;
      }
    },
    *handleUpdatePaymentCard(
      action: PayloadAction<{
        data: any;
        handleNavigate: (url: string) => void;
      }>
    ) {
      const { data, handleNavigate } = action.payload;

      try {
        const response: AxiosResponse = yield call(
          updatePaymentCardDetails,
          data
        );

        if (response?.status !== 200) {
          notify({
            message: "Something went wrong. Please try again.",
            type: ToastTypes.ERROR,
          });
          return;
        }

        const { success, message, returnUrl } = response.data;

        if (success) {
          notify({ message, type: ToastTypes.SUCCESS });

          const updatedData = {
            ...response.data,
            returnUrl: `${data.returnUrl}?${returnUrl}`,
          };

          yield put(PaymentReducerAction.setpaymentResponse(updatedData));
          handleNavigate("UpdatedCard");
        } else {
          notify({ message, type: ToastTypes.ERROR });
          handleNavigate(getRedirectUrl("Refused"));
        }
      } catch (error) {
        console.error("Update card failed:", error);
        notify({
          message: "Failed to update payment card. Please try again later.",
          type: ToastTypes.ERROR,
        });
      }
    },
    *handleSetDefalutSaveCard(
      action: PayloadAction<{
        cardId: string;
      }>
    ) {
      try {
        const { cardId } = action.payload;
        const response: AxiosResponse = yield call(
          setDefalutSaveCardService,
          cardId
        );

        if (response?.status === 200) {
          const cardList: any[] = yield select(userCardDetailsSelector);
          const updatedCardList = cardList?.map((card: any) => {
            if (card._id === cardId) {
              return { ...card, isDefault: true };
            }
            return { ...card, isDefault: false };
          });
          yield put(
            PaymentReducerAction.setUserCardDetails(updatedCardList || [])
          );
        }
      } catch (error) {
        console.error("Update card failed:", error);
      }
    },
    *CheckPaymentDone() {
      try {
        yield put(PaymentReducerAction.setIsLoading(true));
        const response: AxiosResponse = yield call(checkPaymentDone);
        if (response?.status === 200) {
          PaymentReducerAction.setpaymentResponse({});
          yield put(
            PaymentReducerAction.setpaymentResponse(response?.data?.result)
          );
          yield put(PaymentReducerAction.setIsLoading(false));
        }
      } catch (error) {
        yield put(PaymentReducerAction.setIsLoading(false));
        Sentry.captureException(error);
        throw new Error();
      }
    },
    *fetchActiveSubscription() {
      try {
        const response: AxiosResponse = yield call(
          getActiveSubscriptionDetails
        );

        if (response?.status === 200) {
          let mergedResponse = response?.data?.result;
          const folderResponse: AxiosResponse = yield call(
            FetchFolderListService
          );
          if (!mergedResponse) {
            mergedResponse = {
              activePlanDetails: {},
              isFreePlan: false,
              isFolderOverLimit: false,
              isDefaultFolderBoardOverLimit: false,
              defaultFolderId: null,
            };
          }
          // Get current folder data from store
          const folderList: folderLists = folderResponse?.data || {};
          const selectedFolderDetails: folderItem | null | undefined =
            yield select(selectFolderDetailsSelector);

          const isAuthenticated = Boolean(localStorage.getItem("AccessToken"));

          const folderListToShow: folderItem[] = isAuthenticated
            ? folderList?.userFolders ?? []
            : selectedFolderDetails
            ? [selectedFolderDetails]
            : [];

          // Find Default folder
          const defaultFolder: folderItem | undefined = folderListToShow?.find(
            (folder: folderItem) => folder.name === "Default"
          );

          const defaultFolderBoards: number =
            defaultFolder?.boards?.length ?? 0;

          // Over-limit checks
          const isFolderOverLimit: boolean =
            (folderListToShow?.length ?? 0) >=
            (mergedResponse?.activePlanDetails?.folderCount ?? 0);

          const isDefaultFolderBoardOverLimit: boolean =
            defaultFolderBoards >=
            (mergedResponse?.activePlanDetails?.boardCount ?? 0);

          // Append new flags to API response
          mergedResponse = {
            ...mergedResponse,
            isFreePlan: mergedResponse?.activePlanDetails?.planType === "Free",
            isFolderOverLimit,
            isDefaultFolderBoardOverLimit,
            defaultFolderId: defaultFolder?._id,
          };

          // Save in store
          yield put(PaymentReducerAction.setActiveSubscription(mergedResponse));
        }
      } catch (error) {
        console.error("Error fetching active subscription:", error);
        Sentry.captureException(error);
      }
    },
  },
  sagaType: "takeEvery",
});

export const {
  actions: PaymentSagaActions,
  saga: PaymentSaga,
  name: PaymentSagaName,
} = saga;
